import { useCallback, useEffect, useMemo } from "react";

import { useLocation } from "react-router";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import { FormattedMessage } from "@mnemonic/intl";
import { extractErrorMessage } from "@mnemonic/redux-utils";

import {
  LoginBox,
  LoginBoxCallout,
  LoginBoxContent,
  LoginBoxHeader,
  LoginBoxSubHeader,
} from "../../Components/Login/Layout";
import { OpenIDIcon } from "../../Components/OpenIDIcon";
import { useRequestRedirectURIByProviderIDQuery } from "../../Redux/Service/openIdApi";
import { useTypedSelector } from "../../Redux/createStore";
import { setOpenIdFlowSessionStorage } from "../../utils/openIdFlowSessionStorage";
import { LoginOpenIDMissingProvider } from "./LoginOpenIDMissingProvider";
import RedirectWrapper from "./RedirectWrapper";
import { createOpenIdAuthState, resolveOpenIdAuthContext, startOpenIdAuthentication } from "./utils/openId";

/**
 * LoginOpenID
 *
 * Entry point for initiating an OpenID Connect authentication flow.
 *
 * Responsibilities:
 * - Resolve the OpenID provider (from URL or localStorage)
 * - Retrieve the provider redirect URI (pre-auth phase)
 * - Start a new authentication attempt on user intent or auto-auth
 */
export const LoginOpenID = () => {
  const { nextUrl } = useTypedSelector((state) => state.global);
  const location = useLocation();
  const { provider, authenticate } = resolveOpenIdAuthContext(location.search);
  const normalizedNextUrl = nextUrl ?? null;

  useEffect(() => {
    if (provider) {
      localStorage.setItem("openid.provider", provider);
    }
  }, [provider]);

  const preAuthState = useMemo(() => createOpenIdAuthState(normalizedNextUrl), [normalizedNextUrl]);

  const { data, isLoading, error } = useRequestRedirectURIByProviderIDQuery(
    { criteria: { provider, state: preAuthState.state } },
    { skip: !provider || !preAuthState.state },
  );

  const providerURI = data?.data?.providerURI;
  const errorMessage = extractErrorMessage(error);

  const onAuthenticate = useCallback(() => {
    if (!providerURI) return;
    startOpenIdAuthentication(providerURI, preAuthState.state);
  }, [providerURI, preAuthState]);

  /**
   * Auto-authentication support (?authenticate=true)
   */
  useEffect(() => {
    if (authenticate && provider && providerURI) {
      setOpenIdFlowSessionStorage("autoAuthenticate", "true");
      onAuthenticate();
    }
  }, [authenticate, provider, providerURI, onAuthenticate]);

  useEffect(() => {
    setOpenIdFlowSessionStorage("initialNextUrl", String(normalizedNextUrl));
  }, [normalizedNextUrl]);

  return (
    <RedirectWrapper>
      <LoginBox loading={isLoading}>
        <LoginBoxHeader />
        <LoginBoxContent>
          {errorMessage && <LoginBoxCallout type="error">{errorMessage}</LoginBoxCallout>}
          <LoginBoxSubHeader
            method={<FormattedMessage id="login.openid" defaultMessage="Single sign on with OpenID" />}
          />
          <Stack>
            <Grid container sx={{ justifyContent: "center" }}>
              <OpenIDIcon
                sx={({ spacing }) => ({ display: "block", height: 96, mb: spacing(2), mx: "auto", width: 96 })}
                opacity="0.54"
              />
            </Grid>
            {provider ? (
              <Grid container sx={{ justifyContent: "flex-end" }}>
                <Button variant="contained" color="secondary" disabled={isLoading} onClick={onAuthenticate}>
                  <FormattedMessage id="login.authenticate" defaultMessage="Authenticate" />
                </Button>
              </Grid>
            ) : (
              <LoginOpenIDMissingProvider />
            )}
          </Stack>
        </LoginBoxContent>
      </LoginBox>
    </RedirectWrapper>
  );
};
